'use client';

// Stepper del wizard de reserva. Vivia dentro de BookingModal; se saca a su
// propio archivo para que AgendarForm pueda reutilizarlo SIN arrastrar los 41 KB
// del modal al bundle de /agendar. Es presentacional puro: no sabe nada de
// reservas ni toca Supabase.

import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import { rgb, rgba, getForeground } from './bookingShared';

function TimelineStepper({
  steps,
  current,
  brandRGB,
}: {
  steps: string[];
  current: number; // 1-based
  brandRGB: { r: number; g: number; b: number } | null;
}) {
  const total = Math.max(1, steps.length);
  const node = 28; // diámetro px
  const den = Math.max(1, total - 1);
  const pct = Math.max(0, Math.min(1, (current - 1) / den));
  // `as const`: framer-motion 12.34 tipa `Transition` estricto y `type` tiene que
  // ser el literal 'spring', no `string`. Mismos valores que antes.
  const spring = { type: 'spring', stiffness: 320, damping: 30, mass: 0.6 } as const;

  return (
    <div
      className="w-full relative select-none [--node-bg:#fff] dark:[--node-bg:#262626] [--node-border:rgba(0,0,0,0.2)] dark:[--node-border:rgba(255,255,255,0.25)] [--node-text:rgba(0,0,0,0.85)] dark:[--node-text:rgba(255,255,255,0.9)]"
      style={{ paddingLeft: node / 2, paddingRight: node / 2 }}
    >
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))`,
          gridTemplateRows: 'auto 32px',
          rowGap: 6,
          position: 'relative',
        }}
      >
        {/* etiqueta activa */}
        {steps.map((label, i) => {
          const n = i + 1;
          const active = n === current;
          return (
            <div key={`label-cell-${n}`} className="min-w-0">
              <AnimatePresence mode="wait">
                {active && (
                  <motion.div
                    key={`label-${n}`}
                    className="mx-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] border bg-white dark:bg-neutral-800 max-w-[72vw] sm:max-w-[360px] break-words"
                    style={{ borderColor: rgba(brandRGB, 0.25), color: rgba(brandRGB, 0.98) }}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ type: 'tween', ease: 'easeOut', duration: 0.18 }}
                    title={label}
                  >
                    <Icon icon="mdi:progress-check" className="text-[16px]" />
                    <span className="leading-tight">{label}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {/* línea base */}
        <div
          className="col-span-full absolute bg-gray-200 dark:bg-gray-700"
          style={{
            top: 'calc(100% - 32px / 2 - 1.5px)',
            left: 0,
            right: 0,
            height: 3,
            borderRadius: 4,
            zIndex: 0,
          }}
        />
        {/* progreso */}
        <motion.div
          className="col-span-full absolute origin-left"
          style={{
            top: 'calc(100% - 32px / 2 - 1.5px)',
            left: 0,
            right: 0,
            height: 3,
            borderRadius: 4,
            backgroundColor: rgb(brandRGB),
            transform: 'scaleX(0)',
            zIndex: 0,
          }}
          animate={{ transform: `scaleX(${pct})` }}
          transition={spring}
        />

        {/* nodos */}
        {steps.map((_, i) => {
          const n = i + 1;
          const past = n < current;
          const active = n === current;
          // En dark mode usamos neutral-800 como fondo base
          const circleBg = past ? rgb(brandRGB) : 'var(--node-bg, #fff)';
          const circleBorder = active ? rgb(brandRGB) : past ? rgb(brandRGB) : 'var(--node-border, rgba(0,0,0,0.2))';
          // Texto: si está completado usa foreground contrastante, si está activo usa el color primario, sino usa texto por defecto
          const circleText = past ? getForeground(brandRGB) : active ? rgb(brandRGB) : 'var(--node-text, rgba(0,0,0,0.85))';

          return (
            <div key={`node-${n}`} className="min-w-0">
              <div className="w-full h-[32px] grid place-items-center relative z-[1]">
                <motion.div
                  className="rounded-full grid place-items-center text-[12px] font-semibold border"
                  style={{ width: node, height: node }}
                  initial={false}
                  animate={{
                    scale: active ? 1.08 : 1,
                    boxShadow: active ? `0 0 0 4px ${rgba(brandRGB, 0.14)}` : '0 0 0 0px rgba(0,0,0,0)',
                    backgroundColor: circleBg,
                    borderColor: circleBorder,
                    color: circleText,
                  }}
                  transition={spring}
                  title={String(n)}
                >
                  <span className="leading-none">{n}</span>
                </motion.div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TimelineStepper;
export { TimelineStepper };
